/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * certificateExport
 * -----------------
 * Builds a certificate in the browser and downloads it as a PDF or a PNG. One
 * module for every place that does it: the organizer's View dialog
 * (Participants → Certificates) and the participant's "My certificates" /
 * email link (Phase 5).
 *
 *   exportCertificate({ templateUrl, values, format: 'pdf' | 'png', certificateNo })
 *
 * The steps, each exported on its own:
 *   1. loadTemplateHtml(url)       the template file, as text (cached per link)
 *   2. fillTemplate(html, values)  from certificateFiller.js — the caller's real values
 *   3. sanitizeTemplate(html)      no scripts, event handlers or javascript: links
 *   4. certificateDocument(html)   + the certificate fonts, page size A4 landscape
 *   5. renderCertificateCanvas()   laid out in a hidden frame and drawn by the browser itself (html-to-image)
 *   6. downloadCanvas()            → PNG, or → an A4 landscape PDF (jsPDF)
 * Nothing is uploaded or stored: the file is built from the data every time.
 */

import { fillTemplate } from './certificateFiller';

// A4 landscape at 96 dpi; every template is designed at this size.
export const PAGE = { width: 1123, height: 794 };

export const FONTS_CSS = `${process.env.PUBLIC_URL || ''}/certificate-fonts/fonts.css`;
export const EXPORT_SCALE = 2; // 2246 × 1588 px — sharp when printed

const templateCache = new Map();

/** The template's HTML (stored as plain text in file storage), fetched once per link. */
export function loadTemplateHtml(url) {
  if (!url) return Promise.reject(new Error('This certificate has no template.'));
  if (!templateCache.has(url)) {
    const request = fetch(url, { cache: 'no-cache' }).then((resp) => {
      if (!resp.ok) throw new Error('The certificate template could not be loaded.');
      return resp.text();
    });
    templateCache.set(url, request);
    request.catch(() => templateCache.delete(url));
  }
  return templateCache.get(url);
}

const URL_ATTRS = ['href', 'src', 'xlink:href', 'action', 'formaction'];

/**
 * The template without anything that could run: <script>, frames and
 * plug-ins, every on…= handler, and javascript: links. Templates are uploaded
 * by admins, but the export frame has to be readable by this page, so it is
 * cleaned rather than trusted.
 */
export function sanitizeTemplate(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  doc.querySelectorAll('script, iframe, frame, frameset, object, embed, base, meta[http-equiv]').forEach((el) => el.remove());
  doc.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) el.removeAttribute(attr.name);
      else if (URL_ATTRS.includes(name) && /^\s*javascript:/i.test(attr.value)) el.removeAttribute(attr.name);
    });
  });
  return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}

/**
 * A filled template as a complete page: the certificate fonts, and the page
 * pinned to A4 landscape (1123 × 794 CSS px) with no margin, so what is
 * captured is exactly the certificate.
 */
export function certificateDocument(html) {
  const head = `<link rel="stylesheet" href="${FONTS_CSS}">`
    + `<style>html,body{margin:0;padding:0;width:${PAGE.width}px;height:${PAGE.height}px;overflow:hidden;background:#fff}</style>`;
  const text = String(html || '');
  if (/<head[^>]*>/i.test(text)) return text.replace(/<head([^>]*)>/i, `<head$1>${head}`);
  return `<!doctype html><html><head>${head}</head><body>${text}</body></html>`;
}

/** The certificate's page, ready to show or capture: filled, cleaned, with fonts. */
export function buildCertificateHtml(templateHtml, values) {
  return certificateDocument(sanitizeTemplate(fillTemplate(templateHtml, values)));
}

export function certificateFileName(certificateNo, format) {
  const safe = String(certificateNo || 'certificate').replace(/[^A-Za-z0-9_-]/g, '') || 'certificate';
  return `certificate-${safe}.${format === 'png' ? 'png' : 'pdf'}`;
}

function waitForLoad(frame) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('The certificate took too long to prepare.')), 20000);
    frame.onload = () => { clearTimeout(timer); resolve(); };
  });
}

async function waitForAssets(doc) {
  const images = [...doc.images].map((img) => (img.complete ? null : new Promise((resolve) => {
    img.onload = resolve;
    img.onerror = resolve; // a missing logo is left out, not a failure
  })));
  await Promise.all(images.filter(Boolean));
  if (doc.fonts && doc.fonts.ready) await doc.fonts.ready;
}

/**
 * Lays the page out in a hidden frame and draws it to a canvas. The frame may
 * be read by this page (allow-same-origin) but runs no scripts (no
 * allow-scripts), and the HTML is sanitized as well.
 *
 * html-to-image, not html2canvas: it hands the page to the browser to draw
 * (an SVG foreignObject), so text spacing, object-fit and SVG text look
 * exactly as in the preview — html2canvas re-implements text layout and
 * produced uneven spacing and a cropped logo. Its fonts and images are
 * embedded as data first, so the file server must allow this site's origin
 * (CORS_ORIGINS) for the logo to appear.
 */
export async function renderCertificateCanvas(pageHtml, { scale = EXPORT_SCALE } = {}) {
  const { toCanvas } = await import('html-to-image');
  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', 'allow-same-origin');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  Object.assign(frame.style, {
    position: 'fixed', left: '-12000px', top: '0', width: `${PAGE.width}px`, height: `${PAGE.height}px`,
    border: '0', visibility: 'hidden', pointerEvents: 'none',
  });
  const loaded = waitForLoad(frame);
  frame.srcdoc = pageHtml;
  document.body.appendChild(frame);
  try {
    await loaded;
    const doc = frame.contentDocument;
    await waitForAssets(doc);
    return await toCanvas(doc.body, {
      pixelRatio: scale,
      width: PAGE.width,
      height: PAGE.height,
      backgroundColor: '#ffffff',
    });
  } finally {
    frame.remove();
  }
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The captured certificate as a PNG, or as an A4 landscape PDF, as a Blob. */
export async function canvasToBlob(canvas, format) {
  if (format === 'png') {
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('The image could not be created.');
    return blob;
  }
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
  pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, 297, 210);
  return pdf.output('blob');
}

/** The captured certificate saved as a PNG or a PDF file. */
export async function downloadCanvas(canvas, format, filename) {
  saveBlob(await canvasToBlob(canvas, format), filename);
}

/**
 * One certificate as a File (PNG or PDF), named certificate-<number>.<ext>:
 * what Share hands to the phone's share sheet. Nothing is uploaded or stored.
 */
export async function buildCertificateFile({ templateUrl, values, format = 'pdf', certificateNo }) {
  const templateHtml = await loadTemplateHtml(templateUrl);
  const canvas = await renderCertificateCanvas(buildCertificateHtml(templateHtml, values));
  const blob = await canvasToBlob(canvas, format);
  const name = certificateFileName(certificateNo ?? values?.certificate_no, format);
  const type = format === 'png' ? 'image/png' : 'application/pdf';
  return typeof File === 'function' ? new File([blob], name, { type }) : Object.assign(blob, { name });
}

/** Save a built certificate file (from buildCertificateFile) to the device. */
export function saveCertificateFile(file) {
  saveBlob(file, file.name);
}

/**
 * Build and download one certificate. `values` are the certificate's real
 * values (certificateValues() in certificateFiller.js) — the participant's
 * name, the event, activity, organization, results and the course or venue.
 */
export async function exportCertificate({ templateUrl, values, format = 'pdf', certificateNo }) {
  saveCertificateFile(await buildCertificateFile({ templateUrl, values, format, certificateNo }));
}
